<template>
  <div class="pdf-viewer-container">
    <!-- PDF Display Area -->
    <div class="pdf-display" ref="pdfContainer">
      <!-- Loading State - Only show this when the PDF viewer itself is loading, and parent isn't already showing a processing spinner -->
      <div v-if="shouldShowLoadingIndicator" class="pdf-loader">
        <div class="loader"></div>
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- PDF Canvas Layers -->
      <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>
      <canvas ref="highlightCanvas" class="highlight-canvas"></canvas>
      <canvas ref="formCanvas" class="form-canvas" @click="handleFormFieldClick"></canvas>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs';

// Set up the worker configuration
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(workerUrl, window.location.origin).href;

export default {
  name: 'PdfViewer',
  
  props: {
    // PDF URL to load
    pdfUrl: {
      type: String,
      required: true,
    },
    // Form fields to display on the PDF
    fields: {
      type: Array,
      default: () => [],
    },
    // Section colors mapping
    sectionColors: {
      type: Object,
      default: () => ({}),
    },
    // Whether to show field overlays initially
    initialShowFields: {
      type: Boolean,
      default: true,
    },
    // Currently highlighted section
    highlightedSection: {
      type: String,
      default: null,
    },
    // Function to get field group/section
    getFieldGroup: {
      type: Function,
      default: (field) => field.section || 'unknown',
    },
    // External control of current page
    currentPageProp: {
      type: Number,
      default: 1,
    },
  },
  
  data() {
    return {
      // PDF state
      pdfDoc: null,
      pdfPage: null,
      currentPage: 1,
      totalPages: 0,
      pdfScale: 1.5, // Fixed scale for now
      
      // Rendering state
      isLoading: false,
      currentRenderTask: null,
      showFieldOverlays: true,
      
      // Canvas references
      pdfCanvas: null,
      pdfContext: null,
      formCanvas: null,
      formContext: null,
      highlightCanvas: null,
      highlightContext: null,
      pageAnnotations: null,

      // Track highlight animation for cancellation
      highlightAnimationId: null,
    };
  },
  
  computed: {
    loadingMessage() {
      return 'Loading Document…';
    },
    
    // Determines when to show the loading indicator
    shouldShowLoadingIndicator() {
      // Only show our loading indicator if:
      // 1. We are actually loading (isLoading is true)
      // 2. The parent component isn't already showing a processing/loading spinner
      // 3. Our PDF document hasn't been loaded yet or we're in the middle of loading a new page
      return this.isLoading && 
             !this.$parent.isProcessingForm && 
             !this.$parent.loadingPdfDocument &&
             (!this.pdfDoc || this.currentRenderTask);
    },
    
    // Fields for the current page
    currentPageFields() {
      return this.fields.filter(field => field.meta && field.meta.pageNumber === this.currentPage);
    },
  },
  
  watch: {
    pdfUrl() {
      // Reset and load the new PDF when the URL changes
      this.clearHighlightAnimation(); // Clear any active highlights
      this.loadPDF();
    },
    
    fields() {
      // Re-render fields when they change
      // First check if fields array is valid
      if (!this.fields || this.fields.length === 0) {
        console.log('Fields array is empty, skipping field rendering');
        return;
      }
      
      console.log(`Fields updated, received ${this.fields.length} fields`);
      
      // Populate any missing metadata
      if (this.fields.length > 0) {
        console.log('Checking for missing metadata');
        this.populateFieldMetadata();
      }
      
      // Only try to render fields if we have a loaded PDF
      if (this.pdfDoc && this.pdfPage) {
        console.log('PDF is loaded, rendering form fields');
        this.renderFormFields();
      } else {
        console.log('PDF not yet loaded, will render fields when PDF loads');
      }
    },
    
    initialShowFields(newValue) {
      // Always keep fields visible regardless of prop changes
      // No-op - fields should always be visible
    },
    
    highlightedSection() {
      this.renderFormFields();
    },
    
    showFieldOverlays(newValue) {
      console.log(`PdfViewer: showFieldOverlays is always true - showing fields`);
      // Always render fields - they should always be visible
      this.renderFormFields();
    },
    
    currentPage() {
      // Ensure field overlays are updated when page changes
      this.$nextTick(() => {
        this.renderFormFields();
      });
    },
    
    // Watch for changes to the current page prop from parent
    currentPageProp(newPage) {
      console.log(`PdfViewer: currentPageProp changed to ${newPage}`);
      // Only update if different from current page to avoid loops
      if (newPage !== this.currentPage && this.pdfDoc) {
        console.log(`PdfViewer: Rendering page ${newPage} due to prop change`);
        this.renderPage(newPage);
      }
    },
  },
  
  mounted() {
    // Initialize canvas references
    this.pdfCanvas = this.$refs.pdfCanvas;
    this.pdfContext = this.pdfCanvas.getContext('2d');
    
    this.formCanvas = this.$refs.formCanvas;
    this.formContext = this.formCanvas.getContext('2d');
    
    this.highlightCanvas = this.$refs.highlightCanvas;
    this.highlightContext = this.highlightCanvas.getContext('2d');
    
    // Load the PDF if URL is provided
    if (this.pdfUrl) {
      this.loadPDF();
    }
  },
  
  methods: {
    async loadPDF() {
      if (!this.pdfUrl) {
        console.warn('Cannot load PDF: URL is not set');
        return;
      }
      
      console.log(`Loading PDF from URL: ${this.pdfUrl}`);
      
      // Reset state
      this.isLoading = true;
      this.pdfDoc = null;
      this.currentPage = 1;
      
      try {
        // Load the PDF document
        console.log('Starting PDF document loading');
        this.pdfDoc = await pdfjsLib.getDocument({
          url: this.pdfUrl,
          isEvalSupported: false
        }).promise;
        
        this.totalPages = this.pdfDoc.numPages;
        console.log(`PDF loaded successfully, total pages: ${this.totalPages}`);
        
        // Render the first page
        await this.renderPage(1);
        
        // Check if we have field data already loaded from parent
        if (this.fields && this.fields.length > 0) {
          console.log(`PDF loaded with ${this.fields.length} fields already available, rendering fields`);
          this.$nextTick(() => {
            this.renderFormFields();
          });
        } else {
          console.log('PDF loaded but no fields available yet');
        }
        
        // Emit PDF loaded event
        console.log('Emitting pdf-loaded event to parent');
        this.$emit('pdf-loaded', this.pdfDoc);
        
        // Emit a specific loading-complete event to the parent
        this.$emit('loading-complete');
      } catch (error) {
        console.error('Error loading PDF document:', error);
        this.$emit('error', {
          type: 'load',
          message: error.message || 'Failed to load PDF document'
        });
      } finally {
        // Set our local loading state to false
        this.isLoading = false;
      }
    },
    
    async renderPage(pageNumber) {
      if (!this.pdfDoc) {
        console.warn('Cannot render page: PDF document not loaded');
        return;
      }
      
      // Make sure page number is valid
      const pageNum = Math.max(1, Math.min(pageNumber, this.totalPages));
      if (this.currentPage === pageNum && this.pdfPage) {
        console.log(`Page ${pageNum} is already rendered, just updating fields`);
        // Just update the fields
        this.$nextTick(() => this.renderFormFields());
        return;
      }
      
      console.log(`Rendering page ${pageNum} of ${this.totalPages}`);
      this.currentPage = pageNum;
      
      // Cancel any active highlight animation when changing pages
      this.clearHighlightAnimation();
      
      // Cancel any ongoing render task
      if (this.currentRenderTask) {
        try {
          this.currentRenderTask.cancel();
          this.currentRenderTask = null;
        } catch (e) {
          console.error("Error cancelling render task:", e);
        }
      }
      
      // Only set isLoading if we're not showing a processing spinner in the parent
      if (!this.$parent.isProcessingForm) {
        this.isLoading = true;
      }
      
      try {
        // Clear all canvases
        if (this.pdfContext) {
          this.pdfContext.clearRect(0, 0, this.pdfCanvas.width, this.pdfCanvas.height);
        }
        if (this.formContext) {
          this.formContext.clearRect(0, 0, this.formCanvas.width, this.formCanvas.height);
        }
        if (this.highlightContext) {
          this.highlightContext.clearRect(0, 0, this.highlightCanvas.width, this.highlightCanvas.height);
        }
        
        // Reset page annotations for the new page
        this.pageAnnotations = null;
        
        // Get the page - use await to make sure we have the page before proceeding
        console.log(`Fetching page ${pageNum} from PDF document`);
        const pdfPage = await this.pdfDoc.getPage(pageNum);
        this.pdfPage = pdfPage;
        
        // Create viewport with fixed scale
        const viewport = pdfPage.getViewport({ scale: this.pdfScale });
        console.log(`Viewport created: ${viewport.width}x${viewport.height}`);
        
        // Set canvas dimensions - ensure all canvases have the same dimensions
        const width = Math.floor(viewport.width);
        const height = Math.floor(viewport.height);
        
        this.pdfCanvas.width = width;
        this.pdfCanvas.height = height;
        this.formCanvas.width = width;
        this.formCanvas.height = height;
        this.highlightCanvas.width = width;
        this.highlightCanvas.height = height;
        
        console.log(`Set canvas dimensions to ${width}x${height}`);
        
        // Force a browser reflow to ensure canvas sizes are applied
        void this.pdfCanvas.offsetWidth;
        
        // Render the PDF page - create a new render task
        console.log("Starting PDF render task");
        const renderTask = pdfPage.render({
          canvasContext: this.pdfContext,
          viewport: viewport
        });
        
        this.currentRenderTask = renderTask;
        
        // Wait for the render to complete
        console.log("Waiting for render task to complete");
        await renderTask.promise;
        console.log("Render task completed");
        
        this.currentRenderTask = null;
        
        // Check if the page has annotations (form fields)
        try {
          const annotations = await pdfPage.getAnnotations();
          if (annotations && annotations.length > 0) {
            this.pageAnnotations = annotations;
          }
        } catch (annotError) {
          console.warn('Could not read PDF annotations:', annotError);
        }
        
        // Render form fields overlay - always render fields
        // Use nextTick to ensure canvas is ready for additional rendering
        this.$nextTick(() => {
          this.renderFormFields();
        });
        
        // Emit page change event
        this.$emit('page-changed', pageNum);
        
        // Also emit loading completion event
        this.$emit('loading-complete');
      } catch (error) {
        console.error('Error rendering PDF page:', error);
        this.$emit('error', {
          type: 'render',
          message: error.message || 'Failed to render PDF page'
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    renderFormFields() {
      console.log('renderFormFields called, pdfPage:', !!this.pdfPage);
      if (!this.pdfPage) {
        return;
      }
      
      // Clear existing form fields
      if (this.formContext) {
        const canvas = this.formContext.canvas;
        this.formContext.clearRect(0, 0, canvas.width, canvas.height);
      }
      
      const viewport = this.pdfPage.getViewport({ scale: this.pdfScale });
      
      // Get fields for the current page only
      const fields = this.fields.filter(field => 
        field.meta && field.meta.pageNumber === this.currentPage
      );
      
      console.log(`Rendering ${fields.length} fields for page ${this.currentPage}`);
      
      // If we have annotations from the PDF, try to match fields with annotations
      if (this.pageAnnotations && this.pageAnnotations.length > 0) {
        console.log(`Using ${this.pageAnnotations.length} PDF annotations for overlay rendering`);
        this.renderPdfAnnotationOverlays(viewport);
      }
      // Otherwise fall back to our field data if no annotations are available
      else if (fields.length > 0) {
        console.log('Using fallback field data for overlay rendering');
        this.renderCustomFieldOverlays(fields, viewport);
      }
    },
    
    renderPdfAnnotationOverlays(viewport) {
      if (!this.pageAnnotations) return;
      
      this.pageAnnotations.forEach((annotation, index) => {
        // Only process form field annotations
        if (annotation.subtype === 'Widget' && annotation.fieldType) {
          // Get the field's rectangle
          const rect = annotation.rect;
          if (!rect || rect.length !== 4) return;
          
          // Convert PDF coordinates to canvas coordinates
          const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
          
          const fieldName = annotation.fieldName || `field_${index}`;
          
          // Find matching field in our data
          const matchingField = this.findMatchingField(fieldName);
          
          // Determine field color based on type or group
          let sectionColor = '#999999'; // Default gray
          let isHighlighted = false;
          
          if (matchingField) {
            const fieldGroup = this.getFieldGroup(matchingField);
            sectionColor = this.getSectionColor(fieldGroup);
            isHighlighted = this.highlightedSection === fieldGroup || !this.highlightedSection;
          }
          
          // Only consider it a checkbox if it has the correct type and is not a radio button
          const isCheckbox = 
            (annotation.fieldType === 'Btn' && !(annotation.fieldFlags & 32768)) ||
            (matchingField && matchingField.type === 'Btn' && !(matchingField.fieldFlags & 32768));
          
          const isRadio = 
            // Check if it's a radio button by looking at the field flags
            (annotation.fieldType === 'Btn' && (annotation.fieldFlags & 32768)) ||
            (matchingField && matchingField.type === 'Btn' && (matchingField.fieldFlags & 32768));
          
          // Draw box around the form field
          const hasValue = matchingField && matchingField.value;
          const opacity = isHighlighted ? (hasValue ? 0.15 : 0.1) : 0.05;
          
          this.formContext.fillStyle = hasValue ? 
            `${sectionColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` : 
            `rgba(255, 255, 255, ${opacity})`;
          this.formContext.strokeStyle = sectionColor;
          this.formContext.lineWidth = isHighlighted ? 2 : 1;
          
          // Normalize the rectangle (ensure x1,y1 is top-left and x2,y2 is bottom-right)
          const boxX = Math.min(x1, x2);
          const boxY = Math.min(y1, y2);
          const boxWidth = Math.abs(x2 - x1);
          const boxHeight = Math.abs(y2 - y1);
          
          this.formContext.fillRect(boxX, boxY, boxWidth, boxHeight);
          this.formContext.strokeRect(boxX, boxY, boxWidth, boxHeight);
          
          // Draw field value if available
          if (matchingField && matchingField.value) {
            // Add type information for checkbox/radio detection
            if (isCheckbox && !matchingField.type) {
              matchingField.type = 'checkbox';
            } else if (isRadio && !matchingField.type) {
              matchingField.type = 'radio';
            }
            
            // For checkbox/radio fields without a type set
            if (isCheckbox) {
              this.drawCheckbox(matchingField.value, boxX, boxY, boxWidth, boxHeight, fieldName);
            } else if (isRadio) {
              this.drawRadioButton(matchingField.value, boxX, boxY, boxWidth, boxHeight, fieldName);
            } else {
              this.drawFieldValue(matchingField.value, boxX, boxY, boxWidth, boxHeight, matchingField);
            }
          }
          
          // Draw field name (small and semi-transparent)
          // this.formContext.font = '10px Arial';
          // this.formContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
          // this.formContext.fillText(
          //   fieldName.substring(0, 15) + (fieldName.length > 15 ? '...' : ''),
          //   boxX + 2,
          //   boxY + boxHeight - 2
          // );
        }
      });
    },
    
    findMatchingField(fieldName) {
      // Try to match a PDF field name with our custom fields
      const matchedField = this.fields.find(field => {
        return(field.name == fieldName);
      });
      
      return matchedField;
    },
    
    drawFieldValue(value, x, y, width, height, field) {
      // Special handling for checkbox and radio fields
      if (field && field.type === 'Btn') {
        if (field.fieldFlags & 32768) {
          // It's a radio button
          this.drawRadioButton(value, x, y, width, height, field.name);
        } else {
          // It's a checkbox
          this.drawCheckbox(value, x, y, width, height, field.name);
        }
        return;
      }

      const text = value.toString();
      
      // Calculate text position (centered in box)
      this.formContext.font = '12px Arial';
      const textWidth = this.formContext.measureText(text).width;
      const textX = x + (width - textWidth) / 2;
      const textY = y + height / 2;
      
      // Draw white background for text to ensure readability
      const padding = 2;
      // Save current fillStyle
      const currentFillStyle = this.formContext.fillStyle;
      
      // Draw text background
      this.formContext.fillStyle = 'rgba(255, 255, 255, 0.9)';
      this.formContext.fillRect(
        textX - padding,
        textY - 8,
        textWidth + (padding * 2),
        16
      );
      
      // Draw the text in black
      this.formContext.fillStyle = '#000000';
      this.formContext.textBaseline = 'middle';
      this.formContext.fillText(text, textX, textY);
      
      // Restore original fillStyle
      this.formContext.fillStyle = currentFillStyle;
    },
    
    drawCheckbox(value, x, y, width, height, fieldName) {
      // Parse the checkbox value to determine if it's checked
      const isChecked = this.parseCheckboxValue(value);
      
      // Create a checkbox-like visual element
      const boxSize = Math.min(width, height, 16); // Slightly larger checkbox size
      const centerX = x + (width / 2);
      const centerY = y + (height / 2);
      const checkboxX = centerX - (boxSize / 2);
      const checkboxY = centerY - (boxSize / 2);
      
      // Draw checkbox with a more visible background
      this.formContext.fillStyle = 'white';
      this.formContext.fillRect(checkboxX, checkboxY, boxSize, boxSize);
      this.formContext.strokeStyle = '#000000';
      this.formContext.lineWidth = 1.5; // Thicker border
      this.formContext.strokeRect(checkboxX, checkboxY, boxSize, boxSize);
      
      // If checked, draw a more prominent checkmark
      if (isChecked) {
        // Fill with light blue background for better visibility
        this.formContext.fillStyle = 'rgba(200, 230, 255, 0.6)';
        this.formContext.fillRect(checkboxX, checkboxY, boxSize, boxSize);
        
        this.formContext.beginPath();
        this.formContext.strokeStyle = '#000000';
        this.formContext.lineWidth = 2;
        
        // Draw a bold checkmark
        const padding = boxSize * 0.2;
        this.formContext.moveTo(checkboxX + padding, checkboxY + (boxSize / 2));
        this.formContext.lineTo(checkboxX + (boxSize / 2), checkboxY + boxSize - padding);
        this.formContext.lineTo(checkboxX + boxSize - padding, checkboxY + padding);
        this.formContext.stroke();
      }
    },
    
    drawRadioButton(value, x, y, width, height, fieldName) {
      // Create a radio button-like visual element
      const circleSize = Math.min(width, height, 16); // Slightly larger radio size
      const centerX = x + (width / 2);
      const centerY = y + (height / 2);
      const radioX = centerX - (circleSize / 2);
      const radioY = centerY - (circleSize / 2);
      
      // Draw radio button with a more visible background
      this.formContext.fillStyle = 'white';
      this.formContext.beginPath();
      this.formContext.arc(centerX, centerY, circleSize/2, 0, Math.PI * 2);
      this.formContext.fill();
      
      this.formContext.strokeStyle = '#000000';
      this.formContext.lineWidth = 1.5; // Thicker border
      this.formContext.stroke();
      
      // If selected, draw a filled circle
      if (value) {
        // Fill with light blue background for better visibility
        this.formContext.fillStyle = 'rgba(200, 230, 255, 0.6)';
        this.formContext.beginPath();
        this.formContext.arc(centerX, centerY, circleSize/2 - 2, 0, Math.PI * 2);
        this.formContext.fill();
        
        // Draw the inner circle
        this.formContext.fillStyle = '#000000';
        this.formContext.beginPath();
        this.formContext.arc(centerX, centerY, circleSize/4, 0, Math.PI * 2);
        this.formContext.fill();
      }
    },
    
    renderCustomFieldOverlays(fields, viewport) {
      // This is the fallback method that uses our field data rather than PDF annotations
      console.log('Using fallback field rendering with custom field data');
      
      fields.forEach(field => {
        // Ensure field metadata exists
        if (!field.meta) {
          field.meta = { pageNumber: this.currentPage };
        }
        
        // Try to find a corresponding form field from PDF annotations by name
        if (!field.annotation && this.pageAnnotations) {
          field.annotation = this.findMatchingAnnotation(field);
        }
        
        // Get the field box position using our centralized helper
        const box = this.getFieldBoxPosition(field, viewport);
        
        if (box) {
          const fieldGroup = this.getFieldGroup(field);
          const sectionColor = this.getSectionColor(fieldGroup);
          
          // Determine if this field should be highlighted
          const isHighlighted = this.highlightedSection === fieldGroup || !this.highlightedSection;
          const opacity = isHighlighted ? (field.value ? 0.15 : 0.1) : 0.05;
          
          // Draw field highlight box
          this.formContext.fillStyle = field.value ? 
            `${sectionColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` : 
            `rgba(255, 255, 255, ${opacity})`;
          this.formContext.strokeStyle = sectionColor;
          this.formContext.lineWidth = isHighlighted ? 2 : 1;
          
          this.formContext.fillRect(box.x, box.y, box.width, box.height);
          this.formContext.strokeRect(box.x, box.y, box.width, box.height);
          
          // If field has a value, draw it inside the box
          if (field.value) {
            this.drawFieldValue(field.value, box.x, box.y, box.width, box.height, field);
          }
          
          // Draw field name (small and semi-transparent)
          this.formContext.font = '10px Arial';
          this.formContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
          this.formContext.fillText(
            (field.name || field.id || '').substring(0, 15) + 
              ((field.name || field.id || '').length > 15 ? '...' : ''),
            box.x + 2,
            box.y + box.height - 2
          );
        }
      });
    },
    
    findMatchingAnnotation(field) {
      if (!this.pageAnnotations) return null;
      
      return this.pageAnnotations.find(annotation => {
        if (!annotation.fieldName) return false;
        
        // Normalize field names for comparison
        const normalizedAnnotationName = annotation.fieldName.toLowerCase().replace(/\s+/g, '');
        const normalizedFieldName = (field.name || field.id || '').toLowerCase().replace(/\s+/g, '');
        
        return normalizedAnnotationName === normalizedFieldName || 
               normalizedAnnotationName.includes(normalizedFieldName) ||
               normalizedFieldName.includes(normalizedAnnotationName);
      });
    },
    getSectionColor(sectionId) {
      // If the color is already defined in passed sectionColors, use it
      if (this.sectionColors[sectionId]) {
        return this.sectionColors[sectionId];
      }
      
      // Generate a consistent color if not found (using the same algorithm as in FormUploadView)
      const hash = this.hashString(sectionId);
      const hue = hash % 360;
      const color = `hsl(${hue}, 70%, 65%)`;
      
      // We can't modify props directly, so we emit an event to tell the parent to save this color
      this.$emit('update:section-color', { sectionId, color });
      
      return color;
    },
    
    handleFormFieldClick(event) {
      if (!this.pdfPage) return;
      
      const formCanvas = this.$refs.formCanvas;
      if (!formCanvas) return;
      
      // Get the bounding box of the canvas
      const canvasRect = formCanvas.getBoundingClientRect();
      
      // Calculate proper scaling factor between canvas and display size
      const scale = formCanvas.width / canvasRect.width;
      
      // Calculate click position relative to the canvas, accounting for scaling
      const x = (event.clientX - canvasRect.left) * scale;
      const y = (event.clientY - canvasRect.top) * scale;
      
      // Increase padding for easier selection
      const padding = 15;
      
      // Check for PDF annotations first, if available
      if (this.pageAnnotations && this.pageAnnotations.length > 0) {
        const viewport = this.pdfPage.getViewport({ scale: this.pdfScale });
        
        // Find an annotation that contains the click position
        for (const annotation of this.pageAnnotations) {
          if (annotation.subtype === 'Widget' && annotation.fieldType) {
            // Only consider form field annotations
            const rect = annotation.rect;
            if (!rect || rect.length !== 4) continue;
            
            // Convert PDF coordinates to canvas coordinates
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
            
            // Check if click is inside the field plus padding
            const boxX = Math.min(x1, x2) - padding;
            const boxY = Math.min(y1, y2) - padding;
            const boxWidth = Math.abs(x2 - x1) + (padding * 2);
            const boxHeight = Math.abs(y2 - y1) + (padding * 2);
            
            if (x >= boxX && x <= boxX + boxWidth && y >= boxY && y <= boxY + boxHeight) {
              // Find matching field in our data
              const fieldName = annotation.fieldName || '';
              const matchingField = this.findMatchingField(fieldName);
              
              if (matchingField) {
                // Get the field's section
                const fieldGroup = this.getFieldGroup(matchingField);
                
                console.log(`Field clicked: ${matchingField.name}, section: ${fieldGroup}`);
                
                // Emit field click event
                this.$emit('field-click', {
                  field: matchingField,
                  section: fieldGroup
                });
                
                return; // Found a match, exit
              }
            }
          }
        }
      }
      
      console.log('No field found at click position:', x, y);
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        console.log(`Moving to next page: ${this.currentPage + 1}`);
        this.clearHighlightAnimation(); // Clear highlight when changing pages
        this.renderPage(this.currentPage + 1);
      }
    },
    
    previousPage() {
      if (this.currentPage > 1) {
        console.log(`Moving to previous page: ${this.currentPage - 1}`);
        this.clearHighlightAnimation(); // Clear highlight when changing pages
        this.renderPage(this.currentPage - 1);
      }
    },
    
    navigateToPage() {
      console.log(`Navigating to page: ${this.currentPage}`);
      this.clearHighlightAnimation(); // Clear highlight when changing pages
      this.renderPage(this.currentPage);
    },
    
    // Method kept for backward compatibility but modified to always keep fields visible
    toggleFieldOverlays() {
      // Always maintain field visibility
      this.showFieldOverlays = true;
      this.$emit('toggle-overlays', true);
    },
    
    resetPdfState() {
      this.pdfDoc = null;
      this.pdfPage = null;
      this.pdfScale = 1.5;
      this.currentPage = 1;
      this.totalPages = 0;
      this.currentRenderTask = null;
    },
    
    async onPdfLoaded(pdfDoc) {
      console.log('PDF loaded in PdfViewer, checking field structure');
      this.pdfDoc = pdfDoc;
      this.totalPages = pdfDoc.numPages;
      
      // Debug fields with missing metadata
      console.log('Total fields received:', this.fields.length);
      
      // Check if fields need metadata populated
      const fieldsNeedingMeta = this.fields.filter(f => !f.meta || !f.meta.rect);
      if (fieldsNeedingMeta.length > 0) {
        console.log(`${fieldsNeedingMeta.length} fields are missing metadata. Adding default positioning.`);
        
        // Create a more structured approach to add metadata
        this.populateFieldMetadata();
      }
      
      // Emit an event
      this.$emit('pdf-loaded', pdfDoc);
    },
    
    populateFieldMetadata() {
      // Populate missing metadata for fields
      const fieldsPerPage = {};
      
      // First, group fields by page
      this.fields.forEach(field => {
        if (!field.meta) {
          field.meta = { pageNumber: 1 }; // Default to page 1
        }
        
        const pageNum = field.meta.pageNumber || 1;
        if (!fieldsPerPage[pageNum]) {
          fieldsPerPage[pageNum] = [];
        }
        fieldsPerPage[pageNum].push(field);
      });
      
      // For each page, create a grid layout for fields without position data
      Object.keys(fieldsPerPage).forEach(pageNum => {
        const pageFields = fieldsPerPage[pageNum];
        // Check for fields without position data in either format
        const fieldsWithoutPosition = pageFields.filter(f => {
          const hasRectPosition = f.meta.rect && 
                                f.meta.rect.xPosition !== undefined && 
                                f.meta.rect.yPosition !== undefined;
          const hasDirectPosition = f.meta.xPosition !== undefined && 
                                  f.meta.yPosition !== undefined;
          return !hasRectPosition && !hasDirectPosition;
        });
        
        // If no fields need metadata on this page, skip
        if (fieldsWithoutPosition.length === 0) return;
        
        console.log(`Creating positions for ${fieldsWithoutPosition.length} fields on page ${pageNum}`);
        
        // Calculate grid positions
        const columns = 2;
        const rowHeight = 40;
        const colWidth = 250;
        const startX = 50;
        const startY = 100;
        
        fieldsWithoutPosition.forEach((field, index) => {
          const row = Math.floor(index / columns);
          const col = index % columns;
          
          // Add position data directly to meta object
          field.meta.xPosition = startX + (col * colWidth);
          field.meta.yPosition = startY + (row * rowHeight);
          field.meta.width = colWidth - 20;
          field.meta.height = 30;
          
          console.log(`Created position for field: ${field.name || field.id}, page: ${pageNum}, position: [${field.meta.xPosition}, ${field.meta.yPosition}]`);
        });
      });
    },
    
    parseCheckboxValue(value) {
      // Handle boolean values directly
      if (typeof value === 'boolean') {
        return value;
      }
      
      // Handle string values
      if (typeof value === 'string') {
        const lowercased = value.toLowerCase().trim();
        return lowercased === 'true' || 
               lowercased === 'yes' || 
               lowercased === 'x' || 
               lowercased === 'checked' ||
               lowercased === 'on' ||
               lowercased === '1';
      }
      
      // For any other type, coerce to boolean
      return !!value;
    },
    
    highlightField(field) {
      console.log('Highlighting field:', field);
      
      if (!field) {
        console.error('Cannot highlight field: field is null or undefined');
        return;
      }
      
      // Ensure field has meta
      if (!field.meta) {
        console.warn('Field is missing meta property, creating default metadata');
        field.meta = { 
          pageNumber: this.currentPage || 1,
          // Add default normalized coordinates (middle of the page)
          xPosition: 0.5, // Center horizontally
          yPosition: 0.5, // Center vertically 
          width: 0.3,     // 30% of page width
          height: 0.05    // 5% of page height
        };
      } else {
        // If there's meta but no position data, add it
        if (field.meta.xPosition === undefined || field.meta.yPosition === undefined) {
          console.warn('Field has incomplete position data, adding defaults');
          field.meta.xPosition = field.meta.xPosition || 0.5;
          field.meta.yPosition = field.meta.yPosition || 0.5;
          field.meta.width = field.meta.width || 0.3;
          field.meta.height = field.meta.height || 0.05;
        }
      }
      
      // Log current page and target page
      console.log(`Current page: ${this.currentPage}, Field page: ${field.meta.pageNumber}`);
      
      // Check if we need to navigate to a different page
      if (this.currentPage !== field.meta.pageNumber) {
        console.log(`Navigating from page ${this.currentPage} to page ${field.meta.pageNumber} to show field`);
        this.currentPage = field.meta.pageNumber;
        
        // When navigating to a different page, we need to wait for rendering to complete
        this.$nextTick(() => {
          this.renderPage(this.currentPage).then(() => {
            // Extra delay to ensure the page is fully rendered
            setTimeout(() => {
              console.log('Page rendering completed, highlighting field now');
              this.showFieldHighlight(field);
            }, 200);
          });
        });
      } else {
        console.log('Field is on current page, highlighting immediately');
        this.showFieldHighlight(field);
      }
    },
    
    showFieldHighlight(field) {
      console.log('Creating visual highlight for field:', field);
      if (!this.highlightContext) {
        console.error('Cannot highlight: highlightContext not found');
        return;
      }
      
      // Get the current viewport
      const viewport = this.pdfPage ? this.pdfPage.getViewport({ scale: this.pdfScale }) : null;
      if (!viewport) {
        console.warn('No viewport available for highlighting');
        return;
      }
      
      // Check if we need to find the field in annotations first
      if (!field.annotation && this.pageAnnotations && this.pageAnnotations.length > 0) {
        const fieldName = field.name || field.id || '';
        const annotation = this.pageAnnotations.find(ann => 
          ann.fieldName && ann.fieldName.toLowerCase().includes(fieldName.toLowerCase())
        );
        
        if (annotation) {
          // Store the annotation with the field to simplify position calculation
          field.annotation = annotation;
        }
      }
      
      // Calculate the field box position using our centralized method
      const box = this.getFieldBoxPosition(field, viewport);
      if (!box) {
        console.error('Cannot highlight: failed to calculate field position');
        
        // Show notification that we couldn't locate the field
        const notification = document.createElement('div');
        notification.textContent = `Could not locate field: ${field.name || field.id}`;
        notification.style.position = 'absolute';
        notification.style.top = '50%';
        notification.style.left = '50%';
        notification.style.transform = 'translate(-50%, -50%)';
        notification.style.padding = '12px 20px';
        notification.style.backgroundColor = 'rgba(255, 0, 0, 0.8)';
        notification.style.color = 'white';
        notification.style.borderRadius = '5px';
        notification.style.zIndex = '1000';
        notification.style.fontWeight = 'bold';
        
        const container = this.$refs.formCanvas.parentElement;
        container.style.position = 'relative';
        container.appendChild(notification);
        
        setTimeout(() => {
          if (container.contains(notification)) {
            container.removeChild(notification);
          }
        }, 3000);
        
        return;
      }
      
      console.log('Using field box for highlight:', box);
      
      // Draw the highlight using the same position as would be used for normal field rendering
      this.drawCanvasHighlight(box);
    },
    
    drawCanvasHighlight(box) {
      if (!this.highlightContext || !box) return;
      
      // First, cancel any existing animation
      this.clearHighlightAnimation();
      
      const ctx = this.highlightContext;
      ctx.clearRect(0, 0, this.highlightCanvas.width, this.highlightCanvas.height);
      
      // Calculate pulse animation
      const now = Date.now();
      const pulse = Math.sin(now / 200) * 0.5 + 0.5; // Value from 0 to 1
      
      // Use the EXACT same styling approach as in renderPdfAnnotationOverlays
      // but with a fixed color and pulsing effect
      const sectionColor = '#4a90e2'; // Fixed highlight color
      const opacity = 0.2 + (0.2 * pulse); // Pulsing opacity (slightly higher base for visibility)
      
      // Convert opacity to hex - EXACTLY as done in renderPdfAnnotationOverlays
      ctx.fillStyle = `${sectionColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
      ctx.strokeStyle = sectionColor;
      ctx.lineWidth = 2; // Slightly thicker for visibility
      
      // Draw EXACTLY as we do in renderPdfAnnotationOverlays
      ctx.fillRect(box.x, box.y, box.width, box.height);
      ctx.strokeRect(box.x, box.y, box.width, box.height);
      
      // Add pulsing outline for better visibility
      ctx.strokeStyle = `rgba(74, 144, 226, ${0.5 + 0.5 * pulse})`;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 3]);
      ctx.strokeRect(box.x - 2, box.y - 2, box.width + 4, box.height + 4);
      
      // Animate the highlight and store the ID
      this.highlightAnimationId = requestAnimationFrame(() => {
        this.drawCanvasHighlight(box);
      });
      
      // Cancel the animation after 2 seconds
      setTimeout(() => {
        this.clearHighlightAnimation();
      }, 2000);
    },
    
    // Add method to clear highlight animation
    clearHighlightAnimation() {
      if (this.highlightAnimationId) {
        cancelAnimationFrame(this.highlightAnimationId);
        this.highlightAnimationId = null;
      }
      
      // Also clear the canvas
      if (this.highlightContext) {
        this.highlightContext.clearRect(0, 0, this.highlightCanvas.width, this.highlightCanvas.height);
      }
    },
    
    // Add a helper method that centralizes field box position calculation
    getFieldBoxPosition(field, viewport) {
      // Handle case where field has annotation reference
      if (field.annotation && field.annotation.rect && viewport) {
        const rect = field.annotation.rect;
        const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
        return {
          x: Math.min(x1, x2),
          y: Math.min(y1, y2),
          width: Math.abs(x2 - x1),
          height: Math.abs(y2 - y1)
        };
      }
      
      
      // Default position (center of viewport)
      if (viewport) {
        return {
          x: viewport.width / 2 - 100,
          y: viewport.height / 2 - 15,
          width: 200,
          height: 30
        };
      }
      
      return null;
    },
    
    // Add the hashString method to PdfViewer component if it doesn't already have it
    hashString(str) {
      let hash = 0;
      if (str.length === 0) return hash;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    }
  },
  beforeDestroy() {
    // Cancel any active highlight animation when component is destroyed
    this.clearHighlightAnimation();
  }
};
</script>

<style scoped>
.pdf-viewer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.pdf-display {
  flex: 1;
  position: relative;
  overflow: auto;
  display: flex;
  justify-content: center;
  background-color: #f5f5f5;
}

canvas {
  position: absolute;
  top: 20px;
  left: 20px;
  width: calc(100% - 40px);
  height: auto;
}

.form-canvas {
  z-index: 3;
}

.highlight-canvas {
  z-index: 2;
}

.pdf-canvas {
  z-index: 1;
}

.pdf-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 